import {useCallback, useRef, useState} from 'react';
import {Answer} from '../Engine/boolean';
import {getAppContext} from '../Engine/assets';
import {ConsultationSession, CurrentQuestion, ModuleResult} from '../Engine/session';

export type RestoreAnswer = {questionId: string; answerLabel: string};

export type Consultation = {
  current: CurrentQuestion | null;
  results: ModuleResult[];
  isComplete: boolean;
  answeredCount: number;
  // Answers in the active module — drives placeholder fills in the question text.
  observed: ReadonlyMap<string, Answer>;
  submitAnswer: (feature: string, label: string) => void;
  /** Rebuild the session (e.g. on resume): replays `restore` answers by feature
   * lookup until the first unanswered question — the frontier the user left.
   * The module order is fixed, so a resumed session reproduces the same flow. */
  reset: (restore?: ReadonlyArray<RestoreAnswer>) => void;
};

/**
 * React wrapper over the structural-entropy `ConsultationSession`. The session
 * is mutable and lives in a ref; a tick state forces re-render after mutations.
 * `currentQuestion()` is idempotent, so calling it on each render is safe.
 */
export const useConsultation = (): Consultation => {
  const sessionRef = useRef<ConsultationSession | null>(null);
  const answeredRef = useRef(0);
  const [, setTick] = useState(0);
  const rerender = useCallback(() => setTick(t => t + 1), []);

  const build = useCallback((restore?: ReadonlyArray<RestoreAnswer>) => {
    const session = new ConsultationSession(getAppContext());
    let count = 0;
    if (restore && restore.length) {
      const byFeature = new Map(restore.map(a => [a.questionId, a.answerLabel]));
      for (;;) {
        const q = session.currentQuestion();
        if (q === null) {
          break;
        }
        const label = byFeature.get(q.feature);
        if (label === undefined) {
          break; // reached the unanswered frontier
        }
        session.submitAnswer(q.feature, label);
        count += 1;
      }
    }
    sessionRef.current = session;
    answeredRef.current = count;
  }, []);

  if (sessionRef.current === null) {
    build();
  }

  const submitAnswer = useCallback(
    (feature: string, label: string) => {
      sessionRef.current?.submitAnswer(feature, label);
      answeredRef.current += 1;
      rerender();
    },
    [rerender],
  );

  const reset = useCallback(
    (restore?: ReadonlyArray<RestoreAnswer>) => {
      build(restore);
      rerender();
    },
    [build, rerender],
  );

  const session = sessionRef.current as ConsultationSession;
  return {
    current: session.currentQuestion(),
    results: session.results,
    isComplete: session.isComplete,
    answeredCount: answeredRef.current,
    observed: session.activeObserved(),
    submitAnswer,
    reset,
  };
};
